import './App.css';
import React, { useEffect, useState } from 'react';
import Router from './router.js';
import { BrowserRouter } from 'react-router-dom';
import { interCeptor } from './helper/index.js';
import { useSelector, useDispatch } from 'react-redux';
import { clearUserData } from './slices/loginSlice.js';
import LinearProgress from '@mui/material/LinearProgress';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, message: '' }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'An unexpected error occurred.' }
  }

  componentDidCatch(error, info) {
    console.error('App render error:', error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: '#d32f2f', marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: '#555', marginBottom: 24, maxWidth: 480 }}>{this.state.message}</p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ padding: '10px 28px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontSize: 15, cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

const LOGOUT_LIMIT_MS = 20 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
// Shared-PIN staff page -- not the main CMS login, so the CMS auto-logout must never apply.
const PIN_GATED_PATH_PREFIXES = ["/dealer-tip"];

function App() {
  const userInfo = useSelector(state => state.loginSlice)
  const dispatch = useDispatch()
  interCeptor(userInfo.token)

  const [apiLoading, setApiLoading] = useState(false);
  useEffect(() => {
    const handler = (e) => setApiLoading(e.detail.active);
    window.addEventListener("api-loading", handler);
    return () => window.removeEventListener("api-loading", handler);
  }, []);

  // Dealer-tip is its own PIN-gated staff flow (not the CMS login) -- it must never
  // get logged out by the CMS's own inactivity timer.
  const isPinGatedRoute =
    typeof window !== "undefined" &&
    !!window.location.pathname &&
    PIN_GATED_PATH_PREFIXES.some((p) => window.location.pathname.startsWith(p));

  // Logs the session out after 5 minutes of inactivity
  useEffect(() => {
    if (isPinGatedRoute) return;
    let timer = setTimeout(() => {
      dispatch(clearUserData());
      window.location.href = "/login";
    }, LOGOUT_LIMIT_MS);
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        dispatch(clearUserData());
        window.location.href = "/login";
      }, LOGOUT_LIMIT_MS);
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, resetTimer));
    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, resetTimer));
    };
  }, [dispatch]);

  return (
    <ErrorBoundary>
      <div className="App">
        {apiLoading && (
          <LinearProgress
            sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, height: 3 }}
          />
        )}
        <BrowserRouter>
          <Router />
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
